import React, { useEffect, useState, type CSSProperties } from 'react';
import { Route, Routes, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { routes } from '@/lib/routes';
import { operationsApi } from '@/features/workspace/api';
import type { WorkspaceSummary } from '@/features/workspace/types';
import { ModuleGrid } from '@/features/workspace/components/WorkspaceShell';
import { ROLE_MODULE_THEMES } from '@/features/workspace/roleModuleThemes';
import AdminUsersPage from '@/features/owner/components/AdminUsersPage';
import { OperationsRequestsView } from '@/features/operations/components/OperationsRequestsView';
import { OperationsFilesView } from '@/features/operations/components/OperationsFilesView';
import { OperationsNotificationsView } from '@/features/operations/components/OperationsNotificationsView';
import { OperationsArticlesView } from '@/features/operations/components/OperationsArticlesView';
import { RoleAcademicConnectionsOverlay } from '@/features/communications';
import { ResearchToolsPage } from '@/features/research-tools';
import { FileHubPage } from '@/features/file-hub';
import {
  CabinetWorkspaceFrame,
  ScientificGpsBlock,
} from '@/features/cabinet/CabinetWorkspaceFrame';

const ACCENTS = ['#41d2ff', '#49ddb7', '#e2ad3a', '#9e80ff', '#66b7e8', '#ff7a64', '#5da8ff'];

const OperationsDashboardHome: React.FC<{ summary: WorkspaceSummary | null; isPreview: boolean }> = ({
  summary,
  isPreview,
}) => {
  const navigate = useNavigate();

  const kpis = summary?.kpis || [
    { key: 'in_progress', label: { kz: 'Орындалуда', ru: 'В работе', en: 'In progress' }, value: 1, filter_route: 'requests' },
    { key: 'clarification', label: { kz: 'Нақтылау қажет', ru: 'Нужно уточнение', en: 'Clarification' }, value: 1, filter_route: 'requests' },
    { key: 'unstarted', label: { kz: 'Басталмаған', ru: 'Не запущено', en: 'Not started' }, value: 1, filter_route: 'requests' },
    { key: 'total', label: { kz: 'Барлық тарих', ru: 'История', en: 'History' }, value: 5, filter_route: 'requests' },
  ];

  const modules =
    summary?.modules ??
    ([
      {
        key: 'requests',
        order: 1,
        route_id: 'OPS_REQUESTS',
        icon: 'briefcase',
        label: { kz: 'Өтінімдер', ru: 'Заявки в работе', en: 'Requests' },
        description: { kz: '', ru: 'Очередь заявок с сортировкой по приоритету', en: '' },
        tabs: ['registry'],
      },
      {
        key: 'role_access',
        order: 2,
        route_id: 'OPS_ROLE_ACCESS',
        icon: 'shield',
        label: { kz: 'Пайдаланушылар және қолжетімділік', ru: 'Пользователи и доступы', en: 'Users and access' },
        description: { kz: '', ru: 'Пользователи, роли и заявки на доступ', en: '' },
        tabs: ['users'],
      },
      {
        key: 'files',
        order: 3,
        route_id: 'OPS_FILES',
        icon: 'document',
        label: { kz: 'Құжаттар', ru: 'Документы и версии', en: 'Documents' },
        description: { kz: '', ru: 'Файлы заявок, версии и контроль комплектности', en: '' },
        tabs: ['library'],
      },
      {
        key: 'academic_connections',
        order: 4,
        route_id: 'OPS_ACADEMIC_CONNECTIONS',
        icon: 'chat',
        label: { kz: 'Байланыстар', ru: 'Академические связи', en: 'Connections' },
        description: { kz: '', ru: 'Академические контакты и переписка', en: '' },
        tabs: ['registry'],
      },
      {
        key: 'research_tools',
        order: 5,
        route_id: 'OPS_RESEARCH_TOOLS',
        icon: 'chart',
        label: { kz: 'Құралдар', ru: 'Программы и инструменты', en: 'Tools' },
        description: { kz: '', ru: 'Опросы, эксперименты и анализ данных', en: '' },
        tabs: ['toolbox'],
      },
      {
        key: 'notifications',
        order: 6,
        route_id: 'OPS_NOTIFICATIONS',
        icon: 'news',
        label: { kz: 'Хабарламалар', ru: 'Уведомления', en: 'Notifications' },
        description: { kz: '', ru: 'Системные уведомления и напоминания о сроках', en: '' },
        tabs: ['inbox'],
      },
      {
        key: 'b5_files',
        order: 7,
        route_id: 'OPS_B5_FILES',
        icon: 'document',
        label: { kz: 'Файлдар', ru: 'Файлы и документы', en: 'Files' },
        description: { kz: '', ru: 'Единый рабочий архив: материалы, версии, результаты', en: '' },
        tabs: ['archive'],
      },
    ] satisfies WorkspaceSummary['modules']);

  const profile = summary?.profile;

  return (
    <CabinetWorkspaceFrame
      links={{
        notebook: `${routes.operationsPartner}/b5_files`,
        calendar: routes.newRequest,
        meetings: routes.portalSection('education'),
        connections: `${routes.operationsPartner}/academic_connections`,
        alliances: `${routes.operationsPartner}/academic_connections`,
        qa: routes.portalSection('instructions'),
        support: routes.portalSection('contacts'),
      }}
    >
      <section className="role-cabinet">
        {isPreview ? (
          <div className="role-preview-banner">
            <strong>Предпросмотр владельца · Операционный партнёр</strong>
            <span>Режим полного доступа к операционному блоку</span>
          </div>
        ) : null}

        <div className="role-hero" style={{ '--role-accent': '#41d2ff' } as CSSProperties}>
          <div>
            <small>{profile?.academia_id || 'ZA000002'} · Операционный партнёр</small>
            <h2>{profile?.full_name || 'Бекзат Амангельді'}</h2>
            <p>Операционный партнёр · Координатор сервисов и доступов</p>
          </div>
          <div className="role-hero-actions">
            <Link to={routes.assistant} className="role-btn">
              Super AI →
            </Link>
            <Link to={routes.newRequest} className="role-btn primary">
              + Создать заявку
            </Link>
            <button type="button" className="role-btn" onClick={() => navigate(routes.home)}>
              На главную
            </button>
          </div>
        </div>

        <div className="role-statgrid">
          {kpis.map((kpi, index) => (
            <button
              key={kpi.key}
              type="button"
              className="role-stat"
              style={{ '--a': ACCENTS[index % ACCENTS.length], textAlign: 'left', cursor: 'pointer', width: '100%' } as CSSProperties}
              onClick={() => navigate(`/app/operations/${kpi.filter_route || 'requests'}`)}
            >
              <strong>{kpi.value}</strong>
              <span>{kpi.label.ru || kpi.key}</span>
            </button>
          ))}
        </div>

        <ModuleGrid
          modules={modules}
          basePath={routes.operationsPartner}
          theme={ROLE_MODULE_THEMES.operations}
        />
      </section>
      <ScientificGpsBlock openTo={`${routes.operationsPartner}/requests`} />
    </CabinetWorkspaceFrame>
  );
};

export const OperationsPartnerPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [summary, setSummary] = useState<WorkspaceSummary | null>(null);
  const backToCabinet = () => navigate(routes.operationsPartner);

  const isOwner = user?.roles.includes('SYSTEM_OWNER') ?? false;
  const isOperations = user?.roles.includes('OPERATIONS_PARTNER') ?? false;

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const res = await operationsApi.workspace();
        setSummary(res);
      } catch {
        setSummary(null);
      }
    };
    fetchSummary();
  }, []);

  if (!isOwner && !isOperations) {
    return (
      <section className="role-cabinet">
        <div className="role-hero" style={{ '--role-accent': '#ff7b65' } as CSSProperties}>
          <div>
            <small>Доступ ограничен</small>
            <h2>Операционный кабинет</h2>
            <p>Требуется роль Операционного партнёра или Владельца системы.</p>
          </div>
        </div>
      </section>
    );
  }

  const isHome =
    location.pathname === '/app/operations' || location.pathname === '/app/operations/';

  return (
    <div className="role-cabinet-page">
      {isHome ? (
        <OperationsDashboardHome summary={summary} isPreview={isOwner && !isOperations} />
      ) : (
        <Routes>
          <Route path="requests" element={<OperationsRequestsView />} />
          <Route path="role_access" element={<AdminUsersPage breadcrumb="Роли и доступы" embedded />} />
          <Route path="files" element={<OperationsFilesView />} />
          <Route
            path="academic_connections"
            element={
              <RoleAcademicConnectionsOverlay
                isOpen
                onClose={backToCabinet}
                roleContext="OPERATIONS_PARTNER"
                roleTitle="Операционный партнёр"
              />
            }
          />
          <Route
            path="academic_connections/*"
            element={
              <RoleAcademicConnectionsOverlay
                isOpen
                onClose={backToCabinet}
                roleContext="OPERATIONS_PARTNER"
                roleTitle="Операционный партнёр"
              />
            }
          />
          <Route
            path="research_tools"
            element={<ResearchToolsPage onClose={backToCabinet} onBack={backToCabinet} />}
          />
          <Route
            path="research_tools/*"
            element={<ResearchToolsPage onClose={backToCabinet} onBack={backToCabinet} />}
          />
          <Route path="notifications" element={<OperationsNotificationsView />} />
          <Route
            path="b5_files"
            element={<FileHubPage onClose={backToCabinet} onBack={backToCabinet} />}
          />
          <Route
            path="b5_files/*"
            element={<FileHubPage onClose={backToCabinet} onBack={backToCabinet} />}
          />
          <Route path="articles" element={<OperationsArticlesView />} />
          <Route path="*" element={<OperationsDashboardHome summary={summary} isPreview={isOwner && !isOperations} />} />
        </Routes>
      )}
    </div>
  );
};
